import type { HTMLAttributes } from "react";

export type ConfusionVerdict = "pass" | "warn" | "fail";

export interface ConfusionMatrixCell {
  count: number;
  human: ConfusionVerdict;
  machine: ConfusionVerdict;
}

export interface ConfusionMatrixSelection {
  human: ConfusionVerdict;
  machine: ConfusionVerdict;
}

export interface ConfusionMatrixProps extends Omit<
  HTMLAttributes<HTMLDivElement>,
  "onSelect"
> {
  cells: ConfusionMatrixCell[];
  onSelect?: (selection: ConfusionMatrixSelection) => void;
  selected?: ConfusionMatrixSelection | null;
  size?: "md" | "sm";
  total: number;
}

const VERDICTS: ConfusionVerdict[] = ["pass", "warn", "fail"];

function classNames(classes: string[], className: string | undefined): string {
  if (className) {
    classes.push(className);
  }
  return classes.join(" ");
}

function cellKey(human: ConfusionVerdict, machine: ConfusionVerdict): string {
  return `${human}:${machine}`;
}

function isDiagonal(cell: ConfusionMatrixCell): boolean {
  return cell.human === cell.machine;
}

function fullMatrix(cells: ConfusionMatrixCell[]): ConfusionMatrixCell[] {
  const byKey = new Map<string, number>();
  for (const cell of cells) {
    byKey.set(cellKey(cell.human, cell.machine), cell.count);
  }

  return VERDICTS.flatMap((human) =>
    VERDICTS.map((machine) => ({
      count: byKey.get(cellKey(human, machine)) ?? 0,
      human,
      machine,
    })),
  );
}

/**
 * Per-family intensity maxima. Agreement (diagonal) counts usually dwarf
 * disagreement (off-diagonal) counts, so a single global scale renders the
 * disagreement structure — the whole point of the matrix — near-invisible.
 * Each family is normalized to its own maximum instead.
 */
interface FamilyMaxima {
  diagonal: number;
  offDiagonal: number;
}

function familyMaxima(cells: ConfusionMatrixCell[]): FamilyMaxima {
  return {
    diagonal: Math.max(
      1,
      ...cells.filter(isDiagonal).map((cell) => cell.count),
    ),
    offDiagonal: Math.max(
      1,
      ...cells.filter((cell) => !isDiagonal(cell)).map((cell) => cell.count),
    ),
  };
}

function cellPercent(count: number, total: number): string {
  if (total <= 0) {
    return "0%";
  }
  return `${((count / total) * 100).toLocaleString(undefined, {
    maximumFractionDigits: 1,
  })}%`;
}

function verdictLabel(verdict: ConfusionVerdict): string {
  return verdict.charAt(0).toUpperCase() + verdict.slice(1);
}

function isSelected(
  selected: ConfusionMatrixSelection | null | undefined,
  cell: ConfusionMatrixCell,
): boolean {
  return selected?.human === cell.human && selected.machine === cell.machine;
}

function cellClasses(
  cell: ConfusionMatrixCell,
  maxima: FamilyMaxima,
  selected: ConfusionMatrixSelection | null | undefined,
): string {
  const familyMax = isDiagonal(cell) ? maxima.diagonal : maxima.offDiagonal;
  return [
    "marker-confusion-matrix__cell",
    `marker-confusion-matrix__cell--i-${String(
      intensityBucket(cell, familyMax),
    )}`,
    isDiagonal(cell)
      ? "marker-confusion-matrix__cell--diagonal"
      : "marker-confusion-matrix__cell--off-diagonal",
    cell.human === "fail" || cell.machine === "fail"
      ? "marker-confusion-matrix__cell--fail-edge"
      : "",
    isSelected(selected, cell) ? "marker-confusion-matrix__cell--selected" : "",
  ].join(" ");
}

function intensityBucket(cell: ConfusionMatrixCell, familyMax: number): number {
  const intensity = cell.count === 0 ? 0 : cell.count / familyMax;
  return Math.max(0, Math.min(10, Math.ceil(intensity * 10)));
}

function cellLabel(cell: ConfusionMatrixCell, total: number): string {
  return [
    `Human ${cell.human}`,
    `machine ${cell.machine}`,
    `${cell.count.toLocaleString()} coordinates`,
    cellPercent(cell.count, total),
  ].join(", ");
}

export function ConfusionMatrix({
  cells,
  className,
  onSelect,
  selected,
  size = "md",
  total,
  ...props
}: ConfusionMatrixProps) {
  const matrix = fullMatrix(cells);
  const maxima = familyMaxima(matrix);
  const classes = classNames(
    [
      "marker-confusion-matrix",
      `marker-confusion-matrix--${size}`,
      onSelect ? "marker-confusion-matrix--interactive" : "",
    ],
    className,
  );

  if (size === "sm") {
    return (
      <div
        aria-label={`Confusion matrix, ${total.toLocaleString()} comparable coordinates`}
        className={classes}
        role="img"
        {...props}
      >
        {matrix.map((cell) => (
          <span
            aria-hidden="true"
            className={cellClasses(cell, maxima, selected)}
            key={cellKey(cell.human, cell.machine)}
          />
        ))}
      </div>
    );
  }

  return (
    <div
      aria-colcount={4}
      aria-label="Human label by machine mark confusion matrix"
      aria-rowcount={4}
      className={classes}
      role="grid"
      {...props}
    >
      <div className="marker-confusion-matrix__row" role="row">
        <div className="marker-confusion-matrix__corner" role="columnheader">
          Human \ Machine
        </div>
        {VERDICTS.map((machine) => (
          <div
            className="marker-confusion-matrix__axis"
            key={machine}
            role="columnheader"
          >
            {verdictLabel(machine)}
          </div>
        ))}
      </div>
      {VERDICTS.map((human) => (
        <div className="marker-confusion-matrix__row" key={human} role="row">
          <div className="marker-confusion-matrix__axis" role="rowheader">
            {verdictLabel(human)}
          </div>
          {VERDICTS.map((machine) => {
            const cell = matrix.find(
              (candidate) =>
                candidate.human === human && candidate.machine === machine,
            );
            if (!cell) {
              return null;
            }
            const body = (
              <>
                <span className="marker-confusion-matrix__count">
                  {cell.count.toLocaleString()}
                </span>
                <span className="marker-confusion-matrix__percent">
                  {cellPercent(cell.count, total)}
                </span>
              </>
            );
            const key = cellKey(human, machine);
            const commonProps = {
              "aria-label": cellLabel(cell, total),
              className: cellClasses(cell, maxima, selected),
              role: "gridcell",
            };

            // Diagonal cells are agreement — there is nothing to drill into
            // (disagreements are off-diagonal by definition), so they render
            // as static cells with no button affordance.
            return onSelect && !isDiagonal(cell) ? (
              <button
                {...commonProps}
                aria-selected={isSelected(selected, cell)}
                key={key}
                onClick={() => {
                  onSelect({ human, machine });
                }}
                type="button"
              >
                {body}
              </button>
            ) : (
              <div {...commonProps} key={key}>
                {body}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}
